// A1 : doublons à traiter (G06), organisations du périmètre et transporteurs (T03), fiche d'une
// organisation, flotte (T04), selon la charte D-038.

import { useState, type FormEvent } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { formaterNombre, formaterQuantite, LIBELLES_RELATIONS, TYPES_ORGANISATIONS, type TypeRelationOrganisation } from '@carburanttrack/shared';
import { api, messageErreur } from '../../api/client';
import type { InstallationFiche, Organisation } from '../../api/types';
import { aUnRole, useSession } from '../../app/session';
import { Indisponible, Message, MessageErreur, Squelette } from '../../composants/Etats';
import { Icone } from '../../composants/Icones';
import { montrerSurPetitEcran } from '../../composants/selection';
import { libelleCategorie, libelleEtat, libelleProvince, libelleTypeOrganisation } from '../../composants/format';
import { DocumentsObjet } from '../documents/Documents';
import { useDoublons, type Doublon } from '../accueil/communs';
import { EtiquetteException, exceptionMission, resumeOrdre } from '../logistique/communs';
import type { LigneOrdre, SuiviMissions } from '../logistique/types';

// Doublons à traiter (G06) -------------------------------------------------------------------------

export function Doublons() {
  const r = useDoublons();
  const { etat } = useSession();
  // La paire traitée disparaît de la liste : le compte rendu de la décision est affiché au-dessus.
  const [annonce, setAnnonce] = useState<string | null>(null);
  if (r.isLoading) return <Squelette lignes={5} />;
  if (r.error) return <MessageErreur erreur={r.error} />;
  const paires = r.data!.donnees;
  const seuil = Number(r.data!.meta.seuilMetres);
  const decideur = aUnRole(etat, 'administrateur_national');
  return (
    <div className="page-doublons">
      <div className="page-tete">
        <div>
          <span className="contexte">Référentiel des installations</span>
          <h1>Doublons à traiter</h1>
          <p>Détection automatique : nom proche et distance inférieure à {seuil} m.</p>
        </div>
        <span className={paires.length > 0 ? 'etiquette ambre etiquette-forte' : 'etiquette etiquette-forte'}>{paires.length > 0 ? `${paires.length} paire${paires.length > 1 ? 's' : ''} à traiter` : 'Aucune paire à traiter'}</span>
      </div>
      {annonce && <Message type="succes">{annonce}</Message>}
      {paires.length === 0 && (
        <section className="etat-vide" aria-labelledby="titre-aucun-doublon">
          <h2 id="titre-aucun-doublon">Aucun doublon potentiel dans votre périmètre</h2>
          <p>Une paire apparaît ici dès que deux installations au nom proche sont à moins de {seuil} m l’une de l’autre.</p>
        </section>
      )}
      {paires.map((p, i) => (
        <PaireDoublon key={`${p.a.id}-${p.b.id}`} paire={p} rang={i + 1} total={paires.length} seuil={seuil} decideur={decideur} surDecision={setAnnonce} />
      ))}
      <p className="attenue">
        Décision réservée à l’administrateur national. Une paire écartée parce que ce ne sont pas des doublons n’est plus proposée ; la décision et son motif sont inscrits au journal d’audit.
      </p>
    </div>
  );
}

function useFiche(id: string) {
  return useQuery({ queryKey: ['installation', id], queryFn: async () => (await api<InstallationFiche>(`/api/v1/installations/${id}`)).donnees, retry: false });
}

function PaireDoublon({
  paire,
  rang,
  total,
  seuil,
  decideur,
  surDecision,
}: {
  paire: Doublon;
  rang: number;
  total: number;
  seuil: number;
  decideur: boolean;
  surDecision: (annonce: string) => void;
}) {
  const client = useQueryClient();
  const a = useFiche(paire.a.id);
  const b = useFiche(paire.b.id);
  // Par défaut, on conserve l'installation validée et on archive celle qui ne l'est pas.
  const [conserver, setConserver] = useState<'a' | 'b'>(paire.b.etatValidation === 'valide' && paire.a.etatValidation !== 'valide' ? 'b' : 'a');
  const [motif, setMotif] = useState('');
  const [motifEcart, setMotifEcart] = useState('');
  const gardee = conserver === 'a' ? paire.a : paire.b;
  const archivee = conserver === 'a' ? paire.b : paire.a;
  const fusion = useMutation({
    mutationFn: () => api(`/api/v1/installations/${archivee.id}/fusion`, { corps: { cibleId: gardee.id, motif } }),
    onSuccess: async () => {
      surDecision(`« ${archivee.nom} » a été fusionnée dans « ${gardee.nom} » et archivée. Réservoirs et documents ont été rattachés à l’installation conservée.`);
      await client.invalidateQueries({ queryKey: ['doublons'] });
      await client.invalidateQueries({ queryKey: ['installations'] });
      await client.invalidateQueries({ queryKey: ['installation'] });
      await client.invalidateQueries({ queryKey: ['accueil-situation'] });
    },
  });
  const ecartement = useMutation({
    mutationFn: () => api('/api/v1/installations/doublons/ecarter', { corps: { installationAId: paire.a.id, installationBId: paire.b.id, motif: motifEcart } }),
    onSuccess: async () => {
      surDecision(`« ${paire.a.nom} » et « ${paire.b.nom} » ne sont pas des doublons : la paire n’est plus proposée.`);
      await client.invalidateQueries({ queryKey: ['doublons'] });
      await client.invalidateQueries({ queryKey: ['installation'] });
      await client.invalidateQueries({ queryKey: ['accueil-situation'] });
    },
  });
  const fa = a.data;
  const fb = b.data;
  const champs: { libelle: string; va: string | null; vb: string | null }[] = [
    { libelle: 'Nom', va: paire.a.nom, vb: paire.b.nom },
    { libelle: 'Catégorie', va: libelleCategorie(paire.a.categorie), vb: libelleCategorie(paire.b.categorie) },
    { libelle: 'État de validation', va: libelleEtat(paire.a.etatValidation), vb: libelleEtat(paire.b.etatValidation) },
    { libelle: 'Organisation', va: fa?.organisationNom ?? null, vb: fb?.organisationNom ?? null },
    { libelle: 'Province, localité', va: fa ? [libelleProvince(fa.provinceCode), fa.localiteNom].filter(Boolean).join(', ') : null, vb: fb ? [libelleProvince(fb.provinceCode), fb.localiteNom].filter(Boolean).join(', ') : null },
    { libelle: 'Adresse', va: fa?.adresse ?? null, vb: fb?.adresse ?? null },
    { libelle: 'Horaires', va: fa?.horaires ?? null, vb: fb?.horaires ?? null },
    { libelle: 'Réservoirs', va: fa?.reservoirs ? String(fa.reservoirs.length) : null, vb: fb?.reservoirs ? String(fb.reservoirs.length) : null },
  ];
  const differe = (x: string | null, y: string | null) => x !== null && y !== null && x !== y;
  // Schéma de position : la distance réelle placée sur une règle graduée jusqu'au seuil de détection.
  const xB = 40 + Math.min(1, paire.distanceMetres / Math.max(seuil, 1)) * 230;

  return (
    <section className="carte-bloc paire-doublon" aria-labelledby={`titre-paire-${rang}`}>
      <div className="carte-bloc-tete">
        <h2 id={`titre-paire-${rang}`}>
          Paire {rang} sur {total} : {paire.a.nom}
        </h2>
        <span className="etiquette ambre">{paire.distanceMetres} m d’écart</span>
      </div>
      <div className="tableau-conteneur">
        <table className="tableau comparaison">
          <caption className="visuellement-cache">Comparaison des deux installations</caption>
          <thead>
            <tr>
              <th scope="col">Champ</th>
              {(['a', 'b'] as const).map((cote) => {
                const inst = cote === 'a' ? paire.a : paire.b;
                return (
                  <th key={cote} scope="col">
                    {decideur ? (
                      <button type="button" className="choix-conserver" aria-pressed={conserver === cote} onClick={() => setConserver(cote)}>
                        <span className="radio" aria-hidden="true" />
                        <span>
                          <b>{inst.nom}</b>
                          <small>{conserver === cote ? 'Conservée' : 'Archivée après fusion'}</small>
                        </span>
                      </button>
                    ) : (
                      <Link to={`/referentiel/installations/${inst.id}`}>{inst.nom}</Link>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {champs.map((c) => (
              <tr key={c.libelle}>
                <th scope="row">
                  {c.libelle} {differe(c.va, c.vb) && <span className="etiquette ambre">Diffère</span>}
                </th>
                <td>{c.va ?? <span className="indisponible">non renseigné</span>}</td>
                <td>{c.vb ?? <span className="indisponible">non renseigné</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="paire-bas">
        <div className="schema-distance">
          <span className="sur-titre">Position relative</span>
          <svg viewBox="0 0 320 90" role="img" aria-label={`Les deux installations sont distantes de ${paire.distanceMetres} mètres, sous le seuil de ${seuil} mètres.`}>
            <line x1="40" y1="44" x2={xB} y2="44" stroke="var(--attenue)" strokeWidth="1.5" strokeDasharray="4 4" />
            <circle cx="40" cy="44" r="10" fill="var(--primaire)" />
            <circle cx={xB} cy="44" r="10" fill="var(--fond)" stroke="var(--ambre)" strokeWidth="3" />
            <text x={(40 + xB) / 2} y="30" textAnchor="middle" className="schema-texte fort">
              {paire.distanceMetres} m
            </text>
            <text x="40" y="76" textAnchor="middle" className="schema-texte">
              {libelleEtat(paire.a.etatValidation).toLowerCase()}
            </text>
            <text x={xB} y="76" textAnchor="middle" className="schema-texte">
              {libelleEtat(paire.b.etatValidation).toLowerCase()}
            </text>
            <line x1="270" y1="30" x2="270" y2="58" stroke="var(--attenue)" strokeWidth="1" />
            <text x="276" y="48" className="schema-texte petit">
              {seuil} m
            </text>
          </svg>
        </div>
        <div className="decision-doublon">
          {decideur ? (
            <>
              <form
                onSubmit={(e: FormEvent) => {
                  e.preventDefault();
                  fusion.mutate();
                }}
              >
                <b>
                  Conserver « {gardee.nom} » et archiver « {archivee.nom} »
                </b>
                <p className="attenue">L’installation archivée reste consultable. Ses réservoirs et ses documents sont rattachés à celle conservée. L’opération est inscrite au journal d’audit.</p>
                <div className="champ">
                  <label htmlFor={`motif-fusion-${rang}`}>Motif de la fusion (obligatoire)</label>
                  <input id={`motif-fusion-${rang}`} value={motif} onChange={(e) => setMotif(e.target.value)} />
                </div>
                {fusion.error && <MessageErreur erreur={fusion.error} titre="Fusion refusée" />}
                <button type="submit" className="bouton primaire pleine-largeur" disabled={motif.trim().length < 3 || fusion.isPending || ecartement.isPending}>
                  Fusionner
                </button>
              </form>
              <form
                className="ecartement-doublon"
                onSubmit={(e: FormEvent) => {
                  e.preventDefault();
                  ecartement.mutate();
                }}
              >
                <b>Ce ne sont pas des doublons</b>
                <p className="attenue">Les deux installations restent distinctes et la paire n’est plus proposée. La décision est inscrite au journal d’audit.</p>
                <div className="champ">
                  <label htmlFor={`motif-ecart-${rang}`}>Motif (obligatoire)</label>
                  <input id={`motif-ecart-${rang}`} value={motifEcart} onChange={(e) => setMotifEcart(e.target.value)} />
                </div>
                {ecartement.error && <MessageErreur erreur={ecartement.error} titre="Décision refusée" />}
                <button type="submit" className="bouton pleine-largeur" disabled={motifEcart.trim().length < 3 || ecartement.isPending || fusion.isPending}>
                  Ce ne sont pas des doublons
                </button>
              </form>
            </>
          ) : (
            <p className="attenue">Consultation seule : la fusion ou l’écartement sont décidés par l’administrateur national.</p>
          )}
        </div>
      </div>
    </section>
  );
}

// Organisations et transporteurs (T03) -------------------------------------------------------------

interface Vehicule {
  id: string;
  immatriculation: string;
  type: string;
  capaciteLitres: string;
  compartiments: number;
  organisationNom: string;
}

function useVehicules(actif = true) {
  return useQuery({ queryKey: ['vehicules'], queryFn: async () => (await api<Vehicule[]>('/api/v1/vehicules')).donnees, retry: false, enabled: actif });
}

/** Missions en cours du périmètre (même route que la page Livraisons) : activité des transporteurs et des véhicules. */
function useSuiviMissions() {
  return useQuery({ queryKey: ['suivi-missions'], queryFn: async () => (await api<SuiviMissions>('/api/v1/suivi/missions')).donnees, retry: false, refetchInterval: 120_000 });
}

const enMission = (m: LigneOrdre) => m.etat !== 'emis';
const enRetard = (m: LigneOrdre) => (m.retardHeures ?? 0) > 0;

/** Compteur d'activité lu dans le suivi des missions ; motif affiché tant que le suivi n'est pas lisible. */
function CompteurMissions({ suivi, valeur }: { suivi: ReturnType<typeof useSuiviMissions>; valeur: (missions: LigneOrdre[]) => number }) {
  if (suivi.data) return <b>{formaterNombre(valeur(suivi.data.missions))}</b>;
  return <span className="indisponible">{suivi.error ? messageErreur(suivi.error) : 'chargement du suivi'}</span>;
}

export function ListeOrganisations() {
  const [params, setParams] = useSearchParams();
  const type = params.get('type') ?? '';
  const r = useQuery({
    queryKey: ['organisations', type],
    queryFn: async () => (await api<Organisation[]>(`/api/v1/organisations?limite=200${type ? `&type=${type}` : ''}`)).donnees,
  });
  if (type === 'transporteur') return <Transporteurs requete={r} />;
  return (
    <div className="page-organisations">
      <div className="page-tete">
        <div>
          <h1>{type ? `${libelleTypeOrganisation(type)}s` : 'Organisations'}</h1>
          <p>Organisations visibles dans votre périmètre.</p>
        </div>
      </div>
      <div className="barre-filtres">
        <div className="champ">
          <label htmlFor="o-type">Type d’organisation</label>
          <select id="o-type" value={type} onChange={(e) => setParams(e.target.value ? { type: e.target.value } : {})}>
            <option value="">Tous</option>
            {TYPES_ORGANISATIONS.map((t) => (
              <option key={t} value={t}>
                {libelleTypeOrganisation(t)}
              </option>
            ))}
          </select>
        </div>
      </div>
      {r.isLoading && <Squelette lignes={6} />}
      {r.error && <MessageErreur erreur={r.error} />}
      {r.data && <TableauOrganisations organisations={r.data} />}
    </div>
  );
}

function Transporteurs({ requete }: { requete: UseQueryResult<Organisation[]> }) {
  const vehicules = useVehicules();
  const suivi = useSuiviMissions();
  const [choisi, setChoisi] = useState<string | null>(null);
  const liste = requete.data ?? [];
  const selection = liste.find((o) => o.id === choisi) ?? liste[0] ?? null;
  const nombreVehicules = (nom: string) => (vehicules.data ? vehicules.data.filter((v) => v.organisationNom === nom).length : null);
  return (
    <div className="page-transporteurs">
      <div className="page-tete">
        <div>
          <h1>Transporteurs</h1>
          <p>
            {requete.data ? `${liste.length} transporteur${liste.length > 1 ? 's' : ''} visible${liste.length > 1 ? 's' : ''} dans votre périmètre` : ''}
            {vehicules.data ? `, ${vehicules.data.length} véhicules.` : '.'}
          </p>
        </div>
      </div>
      {requete.isLoading && <Squelette lignes={6} />}
      {requete.error && <MessageErreur erreur={requete.error} />}
      {requete.data && liste.length === 0 && <p className="attenue">Aucun transporteur visible avec vos habilitations.</p>}
      {requete.data && liste.length > 0 && (
        <div className="deux-colonnes colonnes-cartes">
          <section className="lateral liste-cartes" aria-label="Liste des transporteurs">
            {liste.map((o) => {
              const n = nombreVehicules(o.nom);
              return (
                <button key={o.id} type="button" className="carte-selection" aria-pressed={selection?.id === o.id} onClick={() => { setChoisi(o.id); montrerSurPetitEcran('titre-transporteur-choisi'); }}>
                  <span className="carte-selection-tete">
                    <span>
                      <b>{o.nom}</b>
                      <small>
                        {libelleTypeOrganisation(o.type)}, {o.provinceCode ? libelleProvince(o.provinceCode) : 'province non renseignée'}, {o.statut === 'actif' ? 'actif' : 'archivé'}
                      </small>
                    </span>
                    {o.estFictive && <span className="etiquette gris">fictive</span>}
                  </span>
                  <span className="mini-compteurs">
                    <span>
                      Véhicules<b>{n === null ? '' : formaterNombre(n)}</b>
                      {n === null && <span className="indisponible">non lisible</span>}
                    </span>
                    <span>
                      En mission
                      <CompteurMissions suivi={suivi} valeur={(missions) => missions.filter((m) => m.transporteur.id === o.id && enMission(m)).length} />
                    </span>
                    <span>
                      En retard
                      <CompteurMissions suivi={suivi} valeur={(missions) => missions.filter((m) => m.transporteur.id === o.id && enRetard(m)).length} />
                    </span>
                  </span>
                </button>
              );
            })}
            <p className="attenue">{liste.some((o) => o.estFictive) ? 'Organisations fictives de l’environnement de présentation.' : ''}</p>
          </section>
          {selection && <DetailTransporteur organisation={selection} vehicules={vehicules.data?.filter((v) => v.organisationNom === selection.nom) ?? null} suivi={suivi} />}
        </div>
      )}
    </div>
  );
}

interface OrganisationDetaillee extends Organisation {
  relations: { id: string; type: string; dateDebut: string; partenaire: { id: string; nom: string; type: string } }[];
}

function useOrganisation(id: string | undefined) {
  return useQuery({ queryKey: ['organisation', id], queryFn: async () => (await api<OrganisationDetaillee>(`/api/v1/organisations/${id}`)).donnees, enabled: Boolean(id) });
}

const libelleRelation = (t: string) => LIBELLES_RELATIONS[t as TypeRelationOrganisation] ?? t.replace(/_/g, ' ');

const MISSIONS_AFFICHEES = 8;

function DetailTransporteur({ organisation: o, vehicules, suivi }: { organisation: Organisation; vehicules: Vehicule[] | null; suivi: ReturnType<typeof useSuiviMissions> }) {
  const detail = useOrganisation(o.id);
  // Missions en cours confiées à ce transporteur dans le périmètre de l'utilisateur, exceptions d'abord.
  const missions = suivi.data ? suivi.data.missions.filter((m) => m.transporteur.id === o.id).sort((a, b) => Number(Boolean(exceptionMission(b))) - Number(Boolean(exceptionMission(a)))) : null;
  return (
    <section className="principal carte-bloc" aria-labelledby="titre-transporteur-choisi">
      <div className="carte-bloc-tete">
        <div>
          <span className="sur-titre">{libelleTypeOrganisation(o.type)}</span>
          <h2 id="titre-transporteur-choisi">{o.nom}</h2>
        </div>
        <span className="attenue">Contact : {o.courriel ?? o.telephone ?? <span className="indisponible">non renseigné</span>}</span>
      </div>
      <h3>Missions en cours</h3>
      {suivi.isLoading && <Squelette lignes={2} />}
      {suivi.error && <Indisponible motif={messageErreur(suivi.error)} />}
      {missions && missions.length === 0 && <p className="attenue">Aucune mission en cours confiée à ce transporteur dans votre périmètre.</p>}
      {missions && missions.length > 0 && (
        <ul className="liste-lignes">
          {missions.slice(0, MISSIONS_AFFICHEES).map((m) => (
            <li key={m.id}>
              <Link to={`/livraisons/${m.id}`}>
                <span>
                  <b>
                    {m.numero}, {resumeOrdre(m)} <EtiquetteException exception={exceptionMission(m)} />
                  </b>
                  <small>
                    {m.etatLibelle}
                    {m.vehicule ? `, ${m.vehicule.immatriculation}` : ''}
                    {m.position ? ` ; ${m.position.libelle}` : ''}
                  </small>
                </span>
                <Icone nom="chevron" />
              </Link>
            </li>
          ))}
        </ul>
      )}
      {missions && missions.length > MISSIONS_AFFICHEES && (
        <p className="attenue">
          {formaterNombre(missions.length - MISSIONS_AFFICHEES)} autre{missions.length - MISSIONS_AFFICHEES > 1 ? 's' : ''} sur la page <Link to="/livraisons">Livraisons</Link>.
        </p>
      )}
      <h3 className="espace-haut">Relations</h3>
      {detail.isLoading && <Squelette lignes={2} />}
      {detail.data && detail.data.relations.length === 0 && <p className="attenue">Aucune relation visible avec vos habilitations.</p>}
      {detail.data && detail.data.relations.length > 0 && (
        <ul className="liste-doublons">
          {detail.data.relations.map((rel) => (
            <li key={rel.id}>
              <span>
                {libelleRelation(rel.type)}
                <small>avec {rel.partenaire.nom}</small>
              </span>
            </li>
          ))}
        </ul>
      )}
      <h3 className="espace-haut">Véhicules</h3>
      {vehicules === null ? (
        <Indisponible motif="flotte non lisible avec vos habilitations" />
      ) : (
        <p>
          {vehicules.length} véhicule{vehicules.length > 1 ? 's' : ''}, capacité totale {formaterQuantite(String(vehicules.reduce((n, v) => n + Number(v.capaciteLitres), 0)), 'L')}.
        </p>
      )}
      <div className="actions espace-haut">
        <Link className="bouton primaire" to={`/referentiel/organisations/${o.id}`}>
          Ouvrir la fiche
        </Link>
        {vehicules !== null && (
          <Link className="bouton" to={`/referentiel/vehicules?transporteur=${encodeURIComponent(o.nom)}`}>
            Voir sa flotte
          </Link>
        )}
      </div>
    </section>
  );
}

export function TableauOrganisations({ organisations }: { organisations: Organisation[] }) {
  return (
    <div className="tableau-conteneur">
      <table className="tableau lignes-48">
        <caption className="visuellement-cache">Organisations</caption>
        <thead>
          <tr>
            <th scope="col">Nom</th>
            <th scope="col">Type</th>
            <th scope="col" className="masquable">
              Province
            </th>
            <th scope="col" className="masquable">
              Statut
            </th>
          </tr>
        </thead>
        <tbody>
          {organisations.length === 0 && (
            <tr>
              <td colSpan={4} className="attenue">
                Aucune organisation visible.
              </td>
            </tr>
          )}
          {organisations.map((o) => (
            <tr key={o.id}>
              <td>
                <Link className="lien-ligne" to={`/referentiel/organisations/${o.id}`}>
                  {o.nom}
                </Link>{' '}
                {o.sigle && <span className="attenue">({o.sigle})</span>} {o.estFictive && <span className="etiquette gris">fictive</span>}
              </td>
              <td className="attenue">{libelleTypeOrganisation(o.type)}</td>
              <td className="masquable">{o.provinceCode ? libelleProvince(o.provinceCode) : <span className="indisponible">non renseignée</span>}</td>
              <td className="masquable">{o.statut === 'actif' ? 'Active' : 'Archivée'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function FicheOrganisation() {
  const { id } = useParams();
  const { etat } = useSession();
  const r = useOrganisation(id);
  if (r.isLoading) return <Squelette lignes={6} />;
  if (r.error) return <MessageErreur erreur={r.error} titre="Organisation introuvable ou non autorisée" />;
  const o = r.data!;
  const administrateur = aUnRole(etat, 'administrateur_national');
  return (
    <div className="fiche-organisation">
      <div className="page-tete">
        <div>
          <span className="contexte">{libelleTypeOrganisation(o.type)}</span>
          <h1>{o.nom}</h1>
          <div className="actions">
            {o.sigle && <span className="attenue">{o.sigle}</span>}
            <span className="etiquette">{o.statut === 'actif' ? 'Active' : 'Archivée'}</span>
            {o.estFictive && <span className="etiquette gris">fictive</span>}
          </div>
        </div>
        {administrateur && (
          <Link className="bouton" to="/administration/organisations">
            Gérer les organisations et relations
          </Link>
        )}
      </div>
      <div className="deux-colonnes">
        <div className="principal">
          <section className="carte-bloc" aria-labelledby="t-relations">
            <h2 id="t-relations">Relations</h2>
            {o.relations.length === 0 ? (
              <p className="attenue">Aucune relation visible avec vos habilitations.</p>
            ) : (
              <ul className="liste-doublons">
                {o.relations.map((rel) => (
                  <li key={rel.id}>
                    <span>
                      <Link to={`/referentiel/organisations/${rel.partenaire.id}`}>{rel.partenaire.nom}</Link>
                      <small>
                        {libelleRelation(rel.type)}, {libelleTypeOrganisation(rel.partenaire.type)}
                      </small>
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <p className="attenue espace-haut">Une relation entre deux distributeurs ne partage jamais de données (règle fixe).</p>
          </section>
          <section className="carte-bloc" aria-labelledby="t-documents-org">
            <h2 id="t-documents-org">Documents</h2>
            <DocumentsObjet objetType="organisation" objetId={o.id} peutDeposer={administrateur || Boolean(etat?.affectations?.some((a) => a.organisationId === o.id))} />
          </section>
        </div>
        <aside className="lateral">
          <section className="carte-bloc" aria-labelledby="t-identite-org">
            <h2 id="t-identite-org">Identité</h2>
            <dl className="paires">
              <div>
                <dt>Sigle</dt>
                <dd>{o.sigle ?? <span className="indisponible">aucun</span>}</dd>
              </div>
              <div>
                <dt>Province du siège</dt>
                <dd>{o.provinceCode ? libelleProvince(o.provinceCode) : <span className="indisponible">non renseignée</span>}</dd>
              </div>
              <div>
                <dt>Adresse</dt>
                <dd>{o.adresse ?? <span className="indisponible">non renseignée</span>}</dd>
              </div>
              <div>
                <dt>Téléphone</dt>
                <dd>{o.telephone ?? <span className="indisponible">non renseigné</span>}</dd>
              </div>
              <div>
                <dt>Courriel</dt>
                <dd>{o.courriel ?? <span className="indisponible">non renseigné</span>}</dd>
              </div>
              <div>
                <dt>Nature</dt>
                <dd>{o.estFictive ? 'Organisation fictive du jeu de présentation' : 'Institution ou organisation réelle'}</dd>
              </div>
            </dl>
          </section>
        </aside>
      </div>
    </div>
  );
}

// Flotte (T04) -------------------------------------------------------------------------------------

interface Conducteur {
  id: string;
  prenom: string;
  nom: string;
  telephone: string | null;
  organisationNom: string;
}

export function Flotte() {
  const [params] = useSearchParams();
  const vehicules = useVehicules();
  const suivi = useSuiviMissions();
  const conducteurs = useQuery({ queryKey: ['conducteurs'], queryFn: async () => (await api<Conducteur[]>('/api/v1/conducteurs')).donnees, retry: false });
  const [onglet, setOnglet] = useState<'vehicules' | 'conducteurs'>('vehicules');
  const [transporteur, setTransporteur] = useState(params.get('transporteur') ?? '');
  const liste = vehicules.data ?? [];
  const transporteurs = [...new Set(liste.map((v) => v.organisationNom))].sort();
  const visibles = liste.filter((v) => !transporteur || v.organisationNom === transporteur);
  const capacite = visibles.reduce((n, v) => n + Number(v.capaciteLitres), 0);
  // Mission en cours d'un véhicule, lue dans le suivi du périmètre : un véhicule engagé par un autre
  // distributeur apparaît « sans mission visible », jamais « disponible ».
  const missionDe = (id: string) => suivi.data?.missions.find((m) => m.vehicule?.id === id) ?? null;
  const libelleActivite = (m: LigneOrdre) => (m.etat === 'emis' ? 'Affecté, à charger' : 'En mission');

  return (
    <div className="page-flotte">
      <div className="page-tete">
        <div>
          <h1>Flotte</h1>
          <p>Véhicules et conducteurs des transporteurs de votre périmètre.</p>
        </div>
        <div className="segments" role="tablist" aria-label="Flotte">
          <button type="button" role="tab" aria-selected={onglet === 'vehicules'} aria-controls="panneau-flotte" onClick={() => setOnglet('vehicules')}>
            Véhicules{vehicules.data && <span className="compte">({liste.length})</span>}
          </button>
          <button type="button" role="tab" aria-selected={onglet === 'conducteurs'} aria-controls="panneau-flotte" onClick={() => setOnglet('conducteurs')}>
            Conducteurs{conducteurs.data && <span className="compte">({conducteurs.data.length})</span>}
          </button>
        </div>
      </div>

      <div id="panneau-flotte" role="tabpanel">
        {onglet === 'vehicules' && (
          <>
            {vehicules.isLoading && <Squelette lignes={5} />}
            {vehicules.error && <MessageErreur erreur={vehicules.error} />}
            {vehicules.data && (
              <>
                <div className="compteurs joints" role="list" aria-label="Synthèse de la flotte">
                  <div role="listitem">
                    Capacité totale<b>{formaterQuantite(String(capacite), 'L')}</b>
                  </div>
                  <div role="listitem">
                    Véhicules<b>{formaterNombre(visibles.length)}</b>
                  </div>
                  <div role="listitem">
                    En mission
                    <CompteurMissions suivi={suivi} valeur={() => visibles.filter((v) => missionDe(v.id)).length} />
                  </div>
                  <div role="listitem">
                    Sans mission visible
                    <CompteurMissions suivi={suivi} valeur={() => visibles.filter((v) => !missionDe(v.id)).length} />
                  </div>
                </div>
                {transporteurs.length > 1 && (
                  <div className="puces espace-bas" role="radiogroup" aria-label="Transporteur">
                    <button type="button" role="radio" className="puce" aria-checked={transporteur === ''} onClick={() => setTransporteur('')}>
                      Tous <span className="compte">{liste.length}</span>
                    </button>
                    {transporteurs.map((t) => (
                      <button key={t} type="button" role="radio" className="puce" aria-checked={transporteur === t} onClick={() => setTransporteur(t)}>
                        {t} <span className="compte">{liste.filter((v) => v.organisationNom === t).length}</span>
                      </button>
                    ))}
                  </div>
                )}
                <div className="tableau-conteneur">
                  <table className="tableau lignes-48">
                    <caption className="visuellement-cache">Véhicules</caption>
                    <thead>
                      <tr>
                        <th scope="col">Immatriculation</th>
                        <th scope="col">Type</th>
                        <th scope="col" className="nombre">
                          Capacité
                        </th>
                        <th scope="col" className="nombre masquable">
                          Compartiments
                        </th>
                        <th scope="col" className="masquable">
                          Transporteur
                        </th>
                        <th scope="col">Activité</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibles.length === 0 && (
                        <tr>
                          <td colSpan={6} className="attenue">
                            Aucun véhicule visible.
                          </td>
                        </tr>
                      )}
                      {visibles.map((v) => {
                        const m = missionDe(v.id);
                        return (
                          <tr key={v.id}>
                            <td>
                              <b>{v.immatriculation}</b>
                            </td>
                            <td>{v.type.replace(/_/g, ' ')}</td>
                            <td className="nombre">{formaterQuantite(v.capaciteLitres, 'L')}</td>
                            <td className="nombre masquable">{v.compartiments}</td>
                            <td className="masquable">{v.organisationNom}</td>
                            <td>
                              {m ? (
                                <Link to={`/livraisons/${m.id}`}>
                                  {libelleActivite(m)}, {m.numero}
                                  {m.position ? ` ; ${m.position.libelle}` : ''}
                                </Link>
                              ) : suivi.data ? (
                                <span className="attenue">Sans mission visible</span>
                              ) : (
                                <Indisponible motif={suivi.error ? messageErreur(suivi.error) : 'chargement du suivi'} />
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <p className="attenue espace-haut">Activité lue dans le suivi des missions de votre périmètre : un véhicule engagé par un autre distributeur apparaît sans mission visible.</p>
              </>
            )}
          </>
        )}
        {onglet === 'conducteurs' && (
          <>
            {conducteurs.isLoading && <Squelette lignes={4} />}
            {conducteurs.error && <p className="message info">Les données personnelles des conducteurs ne sont pas accessibles avec vos habilitations.</p>}
            {conducteurs.data && conducteurs.data.length === 0 && (
              <section className="etat-vide" aria-labelledby="titre-sans-conducteur">
                <span className="pictogramme" aria-hidden="true">
                  <Icone nom="camion" taille={30} epaisseur={1.6} />
                </span>
                <h2 id="titre-sans-conducteur">Aucun conducteur déclaré</h2>
                <p>Les transporteurs n’ont pas encore enregistré leurs conducteurs. Sans conducteur rattaché, une mission ne pourra pas être confirmée sur le terrain.</p>
              </section>
            )}
            {conducteurs.data && conducteurs.data.length > 0 && (
              <div className="tableau-conteneur">
                <table className="tableau lignes-48">
                  <caption className="visuellement-cache">Conducteurs</caption>
                  <thead>
                    <tr>
                      <th scope="col">Conducteur</th>
                      <th scope="col" className="masquable">
                        Téléphone
                      </th>
                      <th scope="col">Transporteur</th>
                    </tr>
                  </thead>
                  <tbody>
                    {conducteurs.data.map((c) => (
                      <tr key={c.id}>
                        <td>
                          <b>
                            {c.prenom} {c.nom}
                          </b>
                        </td>
                        <td className="masquable">{c.telephone ?? <span className="indisponible">non renseigné</span>}</td>
                        <td>{c.organisationNom}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
